import { number, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import type { Assessment } from '@repo/shared/schema';
import { RiskBadge } from './shared';

const labels: Record<string, string> = {
  get SANCTIONS() {
    return t('landing.sanctionsExposure');
  },
  get PEP_SCREENING() {
    return t('extra.politicalExposurePep');
  },
  get ADVERSE_MEDIA() {
    return t('landing.adverseMedia');
  },
  get JURISDICTION_RISK() {
    return t('landing.jurisdictionRisk');
  },
  SOCIAL: 'Occupational / Business Risk',
};
const colors = ['#b8a269', '#bfc7f6', '#9b84a5', '#8b87c4', '#7dbfb0'];
export const AssessmentPanels = ({ assessment }: { assessment?: Assessment }) => {
  useLocale();

  const factors = assessment?.factors ?? [];
  const hasFactors = factors.some((factor) => factor.units > 0);
  let offset = 0;
  return (
    <section className="raven-section">
      <span className="raven-label">{t('workspace.riskLevelJustification')}</span>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="raven-panel">
          <h2>{t('workspace.breakdownOfRiskFactors')}</h2>
          <p className="raven-muted mt-2">
            {t('workspace.relativeContributionOfEvidenceToTheAssessment')}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
            <svg
              aria-label={t('workspace.riskEvidenceContributionChart')}
              className="size-44 shrink-0"
              role="img"
              viewBox="0 0 120 120"
            >
              <circle cx="60" cy="60" fill="none" r="44" stroke="#eeedf5" strokeWidth="22" />
              {factors.map((factor, index) => {
                const length = factor.percentage * 2.7646;
                const start = offset;
                offset += length;
                return (
                  <circle
                    cx="60"
                    cy="60"
                    fill="none"
                    key={factor.category}
                    r="44"
                    stroke={colors[index]}
                    strokeDasharray={`${length} ${276.46 - length}`}
                    strokeDashoffset={-start}
                    strokeWidth="22"
                    transform="rotate(-90 60 60)"
                  >
                    <title>
                      {labels[factor.category]}: {number(factor.percentage)}%
                    </title>
                  </circle>
                );
              })}
            </svg>
            <ul className="space-y-3 text-xs">
              {factors.map((factor, index) => (
                <li className="flex gap-3" key={factor.category}>
                  <span
                    className="mt-1 size-2 rounded-full"
                    style={{ background: colors[index] }}
                  />
                  <span className="flex-1">{labels[factor.category]}</span>
                  <span>{number(factor.percentage)}%</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="raven-muted mt-4 text-center">
            {hasFactors
              ? t('workspace.high3UnitsMedium1InformationalAndDismissed0')
              : t('workspace.noContributingRiskEvidenceRecordedNoPercentageHasBeen')}
          </p>
        </div>
        <div className="raven-panel">
          <h2>{t('workspace.whyThisRiskLevel')}</h2>
          <div className="my-5 flex flex-wrap gap-3">
            <RiskBadge level={assessment?.riskLevel} />
            <span className="raven-muted">
              {(assessment?.reviewComplete ?? !assessment?.provisional)
                ? t('auth.allFindingsReviewed')
                : t('workspace.provisionalOfficerReviewRequired')}
              {assessment && !assessment.coverageComplete && ` · ${t('auth.researchIncomplete')}`}
            </span>
          </div>
          <ul className="space-y-5 text-sm">
            <li>◦ {assessment?.summary ?? t('extra.anAssessmentHasNotBeenGenerated')}</li>
            <li>
              {t('workspace.reviewedFindings')} <RiskBadge level={assessment?.reviewedRiskLevel} />
            </li>
            <li>{t('workspace.companyAndMemberExposureUseTheHighestSupportedSeverity')}</li>
            <li>
              ◦{' '}
              {assessment?.coverageComplete
                ? t('workspace.researchCompleted')
                : t('workspace.someResearchCoverageIsInsufficientAcceptanceRemainsUnavailable')}
            </li>
          </ul>
          <p className="raven-muted mt-6">
            {t('workspace.policy')} {t('workspace.publicWebResearch')}{' '}
          </p>
        </div>
      </div>
    </section>
  );
};
